/**
 * @fileoverview Variant breakdown with proportional revenue bars, metrics, and
 * sortable columns.
 * @module features/home/components/product-statistics/VariantBreakdownTable
 */

import { useMemo, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Thumbnail } from '@/shared/components/ui/Thumbnail'
import { useCompactMoney } from '@/shared/hooks/useCompactMoney'
import { formatPercent, formatInt } from '@/shared/utils/metricsUtils'
import type { VariantMetrics } from '@/features/home/hooks/useProductStatistics'
import type { StatisticsDimensionGroup } from '@/lib/statisticsService'

interface VariantBreakdownTableProps {
  variants: VariantMetrics[]
  dimensionGroup: StatisticsDimensionGroup | null
  className?: string
}

/** Numeric, sortable columns of the breakdown (all map to number fields of VariantMetrics). */
type SortKey = 'sales' | 'cm2' | 'quantity' | 'cm2Margin' | 'cancelledQuantity'
type SortDir = 'asc' | 'desc'

const SORT_COLUMNS: { key: SortKey; labelKey: string }[] = [
  { key: 'sales', labelKey: 'revenue' },
  { key: 'cm2', labelKey: 'profit' },
  { key: 'quantity', labelKey: 'unitsSold' },
  { key: 'cm2Margin', labelKey: 'margin' },
  { key: 'cancelledQuantity', labelKey: 'refunds' },
]

const GRID_COLS =
  'grid grid-cols-[1fr_80px_80px_56px_56px_56px] md:grid-cols-[1fr_100px_100px_72px_72px_72px] gap-2'

export function VariantBreakdownTable({
  variants,
  dimensionGroup,
  className,
}: VariantBreakdownTableProps): ReactElement {
  const { t } = useTranslation('features-home')
  const compactMoney = useCompactMoney()
  const maxRevenue = variants.length > 0 ? Math.max(...variants.map((v) => v.sales)) : 0

  const [sortKey, setSortKey] = useState<SortKey>('sales')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  function handleSort(key: SortKey): void {
    if (key === sortKey) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const sortedVariants = useMemo(() => {
    return [...variants].sort((a, b) => {
      const diff = a[sortKey] - b[sortKey]
      return sortDir === 'asc' ? diff : -diff
    })
  }, [variants, sortKey, sortDir])

  return (
    <div className={cn('rounded-xl border border-border/40 bg-card/30 px-6 py-6', className)}>
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('productStatistics.variantBreakdown.title')}</h3>

      {variants.length === 0 ? (
        <p className="text-sm text-muted-foreground/60 text-center py-8">{t('productStatistics.variantBreakdown.noData')}</p>
      ) : (
        <div className="pt-4 -mx-3">
          {/* Header */}
          <div className={cn(GRID_COLS, 'px-3 pb-2')}>
            <span />
            {SORT_COLUMNS.map((col) => (
              <SortHeader
                key={col.key}
                label={t(`productStatistics.variantBreakdown.columns.${col.labelKey}`)}
                active={sortKey === col.key}
                dir={sortDir}
                onClick={() => handleSort(col.key)}
              />
            ))}
          </div>

          {/* Rows */}
          {sortedVariants.map((v) => {
            const barWidth = maxRevenue > 0 ? (v.sales / maxRevenue) * 100 : 0

            return (
              <div
                key={v.sku}
                className={cn(GRID_COLS, 'items-center px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors')}
              >
                {/* Variant name + bar */}
                <div className="flex items-center gap-3 min-w-0">
                  <Thumbnail src={v.imageUrl ?? dimensionGroup?.imageUrl ?? null} alt={v.label} size={40} />
                  <div className="min-w-0 flex-1">
                    <p className="text-base text-foreground/90 truncate">{v.label}</p>
                    <div className="mt-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-foreground/20 transition-all duration-300"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Revenue */}
                <span className="text-sm text-foreground/90 text-right font-mono">
                  {compactMoney(Math.round(v.sales))}
                </span>

                {/* Profit */}
                <span
                  className={cn(
                    'text-sm text-right font-mono font-medium',
                    v.cm2 >= 0 ? 'text-positive' : 'text-negative'
                  )}
                >
                  {v.cm2 >= 0 ? '+' : ''}{compactMoney(Math.round(v.cm2))}
                </span>

                {/* Units sold (summed line-item quantity, matches the Units Sold KPI) */}
                <span className="text-sm text-muted-foreground text-right font-mono">
                  {formatInt(v.quantity)}
                </span>

                {/* Margin */}
                <span className="text-sm text-muted-foreground text-right font-mono">
                  {formatPercent(v.cm2Margin)}
                </span>

                {/* Refunds (units belonging to fully-cancelled orders) */}
                <span
                  className={cn(
                    'text-sm text-right font-mono',
                    v.cancelledQuantity > 0 ? 'text-negative' : 'text-muted-foreground'
                  )}
                >
                  {formatInt(v.cancelledQuantity)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function SortHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string
  active: boolean
  dir: SortDir
  onClick: () => void
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn(
        'flex items-center justify-end gap-1 w-full select-none cursor-pointer',
        'text-[11px] font-medium uppercase tracking-wider transition-colors',
        active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
      )}
    >
      <span className="truncate">{label}</span>
      {active &&
        (dir === 'asc' ? (
          <ArrowUp className="size-3 shrink-0" />
        ) : (
          <ArrowDown className="size-3 shrink-0" />
        ))}
    </button>
  )
}
